import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface Break {
  id: string;
  date: string;
  start_time: string;
  end_time: string;
  title: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface CreateBreakData {
  date: string;
  start_time: string;
  end_time: string;
  title?: string;
  notes?: string;
}

export const useBreaks = () => {
  const queryClient = useQueryClient();

  const { data: breaks = [], isLoading, refetch } = useQuery({
    queryKey: ["breaks"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("breaks")
        .select("*")
        .order("date", { ascending: true })
        .order("start_time", { ascending: true });
      
      if (error) throw error;
      return data as Break[];
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  const createBreak = useMutation({
    mutationFn: async (newBreak: CreateBreakData) => {
      const { data, error } = await supabase
        .from("breaks")
        .insert([{
          date: newBreak.date,
          start_time: newBreak.start_time,
          end_time: newBreak.end_time,
          title: newBreak.title || "Pause",
          notes: newBreak.notes || null,
        }])
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["breaks"] });
      toast.success("Pause ajoutée avec succès");
    },
    onError: (error) => {
      console.error("Erreur lors de la création de la pause:", error);
      toast.error("Erreur lors de la création de la pause");
    },
  });

  const updateBreak = useMutation({
    mutationFn: async ({ id, ...data }: Partial<Break> & { id: string }) => {
      const { data: updated, error } = await supabase
        .from("breaks")
        .update(data)
        .eq("id", id)
        .select()
        .single();
      
      if (error) throw error;
      return updated;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["breaks"] });
      toast.success("Pause modifiée avec succès");
    },
    onError: (error) => {
      console.error("Erreur lors de la modification de la pause:", error);
      toast.error("Erreur lors de la modification de la pause");
    },
  });

  const deleteBreak = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("breaks")
        .delete()
        .eq("id", id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["breaks"] });
      toast.success("Pause supprimée avec succès");
    },
    onError: (error) => {
      console.error("Erreur lors de la suppression de la pause:", error);
      toast.error("Erreur lors de la suppression de la pause");
    },
  });

  return {
    breaks,
    isLoading,
    refetch,
    createBreak,
    updateBreak,
    deleteBreak,
  };
};
